import { Form } from '@app/components/various/Form';
import { getMarkets } from '@app/ducks';
import { getIsFolderMenuItem, getIsPageMenuItem } from '@app/logic/navigation';
import { paths } from '@app/paths';
import { useEditNavigationSettings } from '@app/services/hooks/settings/useEditNavigationSettings';
import { useSettings } from '@app/services/hooks/settings/useSettings';
import { isDefined } from '@app/utils/is';
import { Settings } from '@typings';
import React from 'react';
import { useSelector } from 'react-redux';

import { SettingsLayout } from '../SettingsLayout';

import { HomePageSection } from './HomePageSection';
import { MenuSection } from './MenuSection';
import { NavigationSettingsContextProvider, useNavigationSettingsContext } from './NavigationSettingsContext';

const NavigationSettingsComponent = () => {
  const allMarkets = useSelector(getMarkets);
  const { data: settings, isPending: isLoadingSettings } = useSettings();

  const { formMethods, addedMenuItems, setSelectedMarket, setSearchPhrase, isLoadingPages, isLoadingMarkets } =
    useNavigationSettingsContext();
  const { mutate: mutateNavigationItems, isPending: isSavingChanges } = useEditNavigationSettings();

  const navigationItems = React.useMemo(() => {
    return settings?.navigationItems ?? [];
  }, [settings?.navigationItems]);

  const navigationItemsWithIds = React.useMemo(
    () =>
      navigationItems.map(parentItem => {
        const { children, id: parentId } = parentItem;

        const childrenWithParentId = children?.map(child => ({
          ...child,
          markets: child.markets?.filter(marketId => allMarkets.some(({ id }) => id === marketId)) ?? child.markets,
          parentId,
        }));

        const childrenToReturn = isDefined(childrenWithParentId) ? { children: childrenWithParentId } : {};

        if (getIsFolderMenuItem(parentItem)) {
          return {
            ...parentItem,
            ...childrenToReturn,
            id: parentId,
          };
        }

        return {
          ...parentItem,
          ...childrenToReturn,
          id: parentId,
          markets: parentItem.markets?.filter(marketId => allMarkets.some(({ id }) => id === marketId)) ?? null,
        };
      }),
    [allMarkets, navigationItems],
  );

  const handleCancel = React.useCallback(() => {
    setSelectedMarket(null);
    setSearchPhrase('');
    formMethods.reset();
  }, [formMethods, setSearchPhrase, setSelectedMarket]);

  const handleSubmit = React.useCallback(
    (value: Settings.NavigationFormFields) => {
      setSelectedMarket(null);
      setSearchPhrase('');
      const items = addedMenuItems.map(item => (getIsPageMenuItem(item) ? { ...item, markets: null } : item));

      mutateNavigationItems({ navigationItems: items, slug: value.slug });
    },
    [addedMenuItems, mutateNavigationItems, setSearchPhrase, setSelectedMarket],
  );

  React.useEffect(() => {
    formMethods.reset(
      { ...formMethods.getValues(), navigationItems: navigationItemsWithIds },
      {
        keepDefaultValues: formMethods.formState.isDirty,
        keepDirty: formMethods.formState.isDirty,
      },
    );
  }, [formMethods, navigationItemsWithIds]);

  const formName = 'NavigationForm';
  const isDisabled = isSavingChanges || isLoadingSettings || isLoadingPages || isLoadingMarkets;

  return (
    <SettingsLayout
      activePath={paths.SETTINGS_NAVIGATION}
      isLoading={isSavingChanges}
      isDisabled={isDisabled}
      hasChanges={formMethods.formState.isDirty}
      onCancel={handleCancel}
      formName={formName}
    >
      <div className="space-y-4">
        <Form formMethods={formMethods} onSubmit={handleSubmit} id={formName}>
          <HomePageSection />
        </Form>
        <MenuSection isLoadingSettings={isLoadingSettings} />
      </div>
    </SettingsLayout>
  );
};

export const NavigationSettings = () => {
  return (
    <NavigationSettingsContextProvider>
      <NavigationSettingsComponent />
    </NavigationSettingsContextProvider>
  );
};
